import {isPending, isFulfilled, isRejected, Middleware, MiddlewareAPI} from '@reduxjs/toolkit';

// Track pending queries to pause polling while fetching/refetching
const pendingQueries = new Set<string>();

// Helper to generate a unique key for a query
const getQueryKey = (action: any): string | null => {
  // RTK Query actions have a type pattern like: 'api/endpointName/pending'
  if (typeof action.type !== 'string' || !action.type.includes('/')) {
    return null;
  }

  const parts = action.type.split('/');
  if (parts.length < 3) {
    return null;
  }

  // Extract endpoint name (e.g., 'getFOECheckinByIdRooms')
  const endpointName = parts[1];

  // Create a key from endpoint name and serialized args
  // Use requestId from meta if available, otherwise serialize the arg
  const argKey = action.meta?.arg?.requestId
    ? action.meta.arg.requestId
    : JSON.stringify(action.meta?.arg?.originalArgs || action.meta?.arg);

  return `${endpointName}:${argKey}`;
};

/**
 * Middleware to pause polling intervals while fetching/refetching
 * Prevents duplicate requests when a query is already in progress
 */
export const pollingPauseMiddleware: Middleware =
  (middlewareApi: MiddlewareAPI) => next => (action: any) => {
    // Check if this is an RTK Query action
    if (!action.type || !action.type.includes('/')) {
      return next(action);
    }

    const queryKey = getQueryKey(action);

    // If we can't identify the query, proceed normally
    if (!queryKey) {
      return next(action);
    }

    // Check if this is a pending action (query starting)
    if (isPending(action)) {
      // Check RTK Query state to see if this query is already in progress
      const state = middlewareApi.getState() as any;
      const apiState = state.api;

      if (apiState?.queries) {
        // RTK Query stores queries with keys matching the query key format
        // Check if this query already exists and is pending
        const endpointName = action.type.split('/')[1];

        // Try to find the query by checking all query keys
        const matchingQuery = Object.entries(apiState.queries).find(
          ([key, query]: [string, any]) => {
            if (!query) return false;
            // Match by endpoint name and compare args
            if (query.endpointName !== endpointName) return false;

            const queryArgs = query.originalArgs ?? query.arg;
            const actionArgs =
              action.meta?.arg?.originalArgs ?? action.meta?.arg;

            // Compare serialized args
            return JSON.stringify(queryArgs) === JSON.stringify(actionArgs);
          },
        );

        // If query is already pending (status === 'pending'), block this polling trigger
        if (matchingQuery) {
          const queryState = matchingQuery[1] as any;
          if (queryState?.status === 'pending') {
            // Skip this action - polling is paused while fetch is in progress
            // Return without calling next() to prevent the action from being dispatched
            return action;
          }
        }
      }

      // Track pending query
      pendingQueries.add(queryKey);
    }

    // Track when queries complete (fulfilled or rejected)
    if (isFulfilled(action) || isRejected(action)) {
      // Remove from pending set
      pendingQueries.delete(queryKey);
    }

    // Continue with the action
    return next(action);
  };

